import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useMemo, useRef, useState } from "react";
import type { NetworkDestination } from "../../../../db/schema";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { formatTimestamp } from "../../../lib/formatTimestamp";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../../lib/urls";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Dropdown } from "../../Dropdown";
import { Icon } from "../../Icon";
import { Input } from "../../Input";
import { ListTable } from "../../ListTable";
import { StatusBadge } from "../../StatusBadge";
import { TabbedModal } from "../../TabbedModal";
import { Text } from "../../Text";
import {
  Card,
  FieldLabel,
  HoverActions,
  ListBox,
  NeutralIconBadge,
  PillBadge,
  RowNameContent,
  RowNameLayout,
  RowNameLine,
  SectionHeading,
} from "../shared";
import { DangerSection } from "../DangerSection";
import { DestinationInput } from "./DestinationInput";
import { useDestinationDraft } from "./destinationDraft";
import { RouteSpacePicker } from "./RouteSpacePicker";
import { NetworkAccessTab } from "./NetworkAccessTab";
import { NetworkConnectionPanel } from "./NetworkConnectionStatus";
import { NetworkProvisioning } from "./NetworkProvisioning";

type Tab = "settings" | "access";

const TABS = [
  { key: "settings", label: "Settings", icon: "settings-2" },
  { key: "access", label: "Access", icon: "lock" },
] as const;

const Stack = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

function typeLabel(type: NetworkDestination["type"]): string {
  return type === "hostnameGlob" ? "Hostname pattern" : "CIDR";
}

export function NetworkDetailModal({
  networkId,
  onClose,
}: {
  networkId: string;
  onClose: () => void;
}) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const [networks] = useQuery(queries.networks());
  const network = useMemo(
    () => networks.find((n) => n.id === networkId),
    [networks, networkId]
  );
  const { tab } = useCurrentUrl(routes.modal.network, { id: networkId });
  const activeTab = tab as Tab;
  const setActiveTab = (next: Tab) =>
    navigate(routes.modal.network, { id: networkId, tab: next });
  const canManageNetworks = useHasAccess("tenant:networks_manage");
  const canWrite = useHasAccess("network:write", networkId);
  const canManage = canWrite || canManageNetworks;
  const canManageRoutes = useHasAccess("tenant:egress_routes_manage");
  const [newSpaceId, setNewSpaceId] = useState<string | null>(null);
  const [newType, setNewType] =
    useState<NetworkDestination["type"]>("hostnameGlob");
  const destinationInputRef = useRef<HTMLInputElement>(null);
  const destinationDraft = useDestinationDraft(newType);
  const [nameDraft, setNameDraft] = useState<string | null>(null);

  if (!network) {
    return null;
  }

  const config = network.configuration as {
    gatewayEndpoint?: string;
    clientCertSha256?: string;
    publicHostname?: string;
  } | null;
  const isTunnel = network.identifier === "tunnel";

  const saveName = () => {
    if (nameDraft === null) return;
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== network.name) {
      z.mutate(
        sharedMutators.updateNetwork({ networkId: network.id, name: trimmed })
      );
    }
    setNameDraft(null);
  };

  const addRoute = (destination: NetworkDestination) => {
    z.mutate(
      sharedMutators.createEgressRoute({
        id: nanoid(12),
        networkId: network.id,
        destination,
        enforced: false,
        spaceId: newSpaceId,
      })
    );
    destinationDraft.setDraft("");
    destinationInputRef.current?.focus();
  };

  const routeItems = network.egressRoutes
    .filter((r) => r.disabledAt == null)
    .map((route) => ({
      key: route.id,
      name: (
        <RowNameLayout>
          <NeutralIconBadge iconName="pin" />
          <RowNameContent>
            <RowNameLine>
              <Text variant="text-regular-md" ellipsis>
                {route.destination}
              </Text>
              {route.enforced && (
                <StatusBadge color="green">Enforced</StatusBadge>
              )}
            </RowNameLine>
            <Text variant="text-small" color="var(--text-secondary)" ellipsis>
              {typeLabel(route.destinationType)}
            </Text>
          </RowNameContent>
        </RowNameLayout>
      ),
      scope: canManageRoutes ? (
        <RouteSpacePicker
          value={route.spaceId}
          onChange={(spaceId) =>
            z.mutate(
              sharedMutators.updateEgressRoute({
                id: route.id,
                spaceId,
              })
            )
          }
        />
      ) : (
        <RouteSpacePicker value={route.spaceId} />
      ),
      actions: (hasMouse: boolean) =>
        canManageRoutes ? (
          <HoverActions visible={hasMouse}>
            <Dropdown
              opener={<Button variant="secondary" icon="more" />}
              align="bottom"
              minWidth={180}
              sections={[
                {
                  items: [
                    {
                      type: "button" as const,
                      left: <Icon name="shield-check" size={16} />,
                      label: route.enforced ? "Stop enforcing" : "Enforce",
                      onClick: () =>
                        z.mutate(
                          sharedMutators.updateEgressRoute({
                            id: route.id,
                            enforced: !route.enforced,
                          })
                        ),
                    },
                    {
                      type: "button" as const,
                      left: <Icon name="trash" size={16} />,
                      label: "Remove",
                      onClick: () =>
                        z.mutate(
                          sharedMutators.deleteEgressRoute({ id: route.id })
                        ),
                    },
                  ],
                },
              ]}
            />
          </HoverActions>
        ) : null,
    }));

  return (
    <TabbedModal
      icon={<NeutralIconBadge iconName="runs" />}
      title={nameDraft ?? network.name}
      titleAfter={
        network.disabledAt != null ? <PillBadge>Disabled</PillBadge> : null
      }
      tabs={TABS.map((t) => ({
        ...t,
        to: makeUrl(routes.modal.network, { id: networkId, tab: t.key }),
      }))}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onClose={onClose}
      footerRight={
        <Button size="l" onClick={onClose}>
          Done
        </Button>
      }
    >
      <Stack style={{ display: activeTab === "access" ? "none" : "flex" }}>
        {isTunnel && <NetworkConnectionPanel network={network} />}
        <Card>
          <FieldLabel>
            <Box horizontal={8} vertical={10}>
              <Text variant="text-small-md">Name</Text>
            </Box>
            <Input
              aria-label="Network name"
              readOnly={!canManage}
              value={nameDraft ?? network.name}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.currentTarget.blur();
                }
              }}
              style={{ width: "100%" }}
            />
          </FieldLabel>

          {isTunnel ? (
            <FieldLabel>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Gateway endpoint</Text>
              </Box>
              <Input
                value={config?.gatewayEndpoint ?? "—"}
                disabled
                readOnly
                style={{ width: "100%", cursor: "default" }}
              />
            </FieldLabel>
          ) : (
            config?.publicHostname && (
              <FieldLabel>
                <Box horizontal={8} vertical={10}>
                  <Text variant="text-small-md">Public hostname</Text>
                </Box>
                <Input
                  value={config.publicHostname}
                  disabled
                  readOnly
                  style={{ width: "100%", cursor: "default" }}
                />
              </FieldLabel>
            )
          )}

          {isTunnel && config?.clientCertSha256 && (
            <FieldLabel>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Client cert fingerprint</Text>
              </Box>
              <Input
                value={config.clientCertSha256}
                disabled
                readOnly
                style={{
                  width: "100%",
                  fontFamily: "var(--font-mono)",
                  cursor: "default",
                }}
              />
            </FieldLabel>
          )}

          <FieldLabel>
            <Box horizontal={8} vertical={10}>
              <Text variant="text-small-md">Created</Text>
            </Box>
            <Input
              value={formatTimestamp(network.createdAt)}
              disabled
              readOnly
              style={{ width: "100%", cursor: "default" }}
            />
          </FieldLabel>
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Destinations</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              IP ranges (CIDR) or hostname globs reachable through this tunnel.
            </Text>
          </SectionHeading>
          {canManageRoutes && (
            <>
              <RouteSpacePicker value={newSpaceId} onChange={setNewSpaceId} />
              <DestinationInput
                type={newType}
                onTypeChange={setNewType}
                draft={destinationDraft}
                onAdd={addRoute}
                inputRef={destinationInputRef}
              />
            </>
          )}
          {routeItems.length > 0 && (
            <ListBox
              data-compact="true"
              style={{ flex: "none", overflow: "hidden" }}
            >
              <ListTable
                headers={{ name: "", scope: "Space", actions: "" }}
                columns={{ name: "md", scope: "md" }}
                hideHeaders
                items={routeItems}
              />
            </ListBox>
          )}
        </Card>

        {isTunnel && <NetworkProvisioning networkId={networkId} />}

        {canManage && (
          <DangerSection
            title="Delete network"
            description="Removes this network. Steps that route through it will stop reaching their destinations."
            actionLabel="Delete network"
            confirm={{
              confirmLabel: "Delete",
              description: `“${network.name}” will be removed. Steps that route through this network will stop reaching their destinations.`,
              onConfirm: () => {
                z.mutate(
                  sharedMutators.deleteNetwork({ networkId: network.id })
                );
                onClose();
              },
            }}
          />
        )}
      </Stack>

      <Stack style={{ display: activeTab === "access" ? "flex" : "none" }}>
        <NetworkAccessTab networkId={network.id} canManage={canManage} />
      </Stack>
    </TabbedModal>
  );
}
